"use client"

import { createCommands, createSuggestions } from "@/constants/command-items"
import { useShortcut } from "@/hooks/useShortcut"
import { authClient } from "@/lib/auth-client"
import { useAppStore } from "@/store"
import { cn } from "@preview-kit/ui/lib/utils"
import Fuse from "fuse.js"
import { Search } from "lucide-react"
import React, { useEffect, useRef, useState } from "react"
import { useShallow } from "zustand/shallow"
import { CommandItemComponent } from "./command-item"

export default function CommandListView() {
	const {
		setNavigation,
		setSearchQuery,
		navigation,
		searchQuery,
		commandOpen,
		setCommandOpen,
	} = useAppStore(
		useShallow((state) => ({
			navigation: state.navigation,
			searchQuery: state.searchQuery,
			setNavigation: state.setNavigation,
			setSearchQuery: state.setSearchQuery,
			commandOpen: state.commandOpen,
			setCommandOpen: state.setCommandOpen,
		})),
	)

	const { setAnimateClick } = useAppStore(
		useShallow((state) => ({
			setAnimateClick: state.setAnimateClick,
		})),
	)

	// Add ref for input to handle delayed focus
	const inputRef = useRef<HTMLInputElement>(null)

	// Handle delayed focus to prevent layout shift during animation
	useEffect(() => {
		if (commandOpen && inputRef.current) {
			inputRef.current?.focus({ preventScroll: true })
		}
	}, [commandOpen])

	const navigateToView = (view: React.ReactNode, name: string) => {
		setAnimateClick()
		setTimeout(() => {
			setNavigation({
				currentView: view,
				breadcrumb: [...navigation.breadcrumb, name],
				viewStack: [...navigation.viewStack, navigation.currentView],
			})
			setSearchQuery("")
		}, 40)
	}

	const handlers = useAppStore(
		useShallow((state) => ({
			setIsCommentMode: state.setIsCommentMode,
			setCommandOpen: state.setCommandOpen,
			setCommentsOpen: state.setCommentsOpen,
		})),
	)
	const { data } = authClient.useSession()
	const activeSuggestions = handlers ? createSuggestions(handlers) : []
	const activeCommands = handlers ? createCommands(handlers, !!data?.user) : []

	// Fuse.js configuration
	const fuseOptions = {
		keys: ["name"],
		threshold: 0.4,
		includeScore: true,
		ignoreLocation: true,
		useExtendedSearch: true,
	}

	const suggestionFuse = new Fuse(activeSuggestions, fuseOptions)
	const commandFuse = new Fuse(activeCommands, fuseOptions)

	// Conditional Search: Show all if query empty
	const suggestionResults = searchQuery.trim()
		? suggestionFuse.search(searchQuery)
		: activeSuggestions.map((item) => ({ item }))

	const commandResults = searchQuery.trim()
		? commandFuse.search(searchQuery)
		: activeCommands.map((item) => ({ item }))

	const filteredSuggestions = suggestionResults.map((result) => result.item)
	const filteredCommands = commandResults.map((result) => result.item)

	const allItems = [...filteredSuggestions, ...filteredCommands]
	const [selectedIndex, setSelectedIndex] = useState(0)

	const scrollContainerRef = useRef<HTMLDivElement>(null)
	const selectedItemRefs = useRef<(HTMLElement | null)[]>([])

	useEffect(() => {
		setSelectedIndex(0)
	}, [searchQuery])

	useEffect(() => {
		const selectedElement = selectedItemRefs.current[selectedIndex]
		if (selectedElement && scrollContainerRef.current) {
			const container = scrollContainerRef.current
			const elementRect = selectedElement.getBoundingClientRect()
			const containerRect = container.getBoundingClientRect()
			const totalItems = allItems.length

			const isAbove = elementRect.top < containerRect.top
			const isBelow = elementRect.bottom > containerRect.bottom

			if (isAbove || isBelow) {
				if (selectedIndex === 0) {
					container.scrollTop = 0
				} else if (selectedIndex === totalItems - 1) {
					container.scrollTop = container.scrollHeight - container.clientHeight
				} else {
					selectedElement.scrollIntoView({
						behavior: "auto",
						block: "nearest",
						inline: "nearest",
					})
				}
			}
		}
	}, [selectedIndex, allItems.length])

	useEffect(() => {
		function handleKeyNavigation(e: KeyboardEvent) {
			if (e.key === "ArrowDown") {
				e.preventDefault()
				if (selectedIndex < allItems.length - 1) {
					setSelectedIndex((prev) => prev + 1)
				}
			} else if (e.key === "ArrowUp") {
				e.preventDefault()
				if (selectedIndex > 0) {
					setSelectedIndex((prev) => prev - 1)
				}
			}
		}
		document.addEventListener("keydown", handleKeyNavigation)
		return () => {
			document.removeEventListener("keydown", handleKeyNavigation)
		}
	}, [selectedIndex, allItems.length])

	useEffect(() => {
		const totalItems = filteredSuggestions.length + filteredCommands.length
		selectedItemRefs.current = new Array(totalItems).fill(null)
	}, [filteredSuggestions.length, filteredCommands.length])

	useShortcut("ENTER", () => {
		if (commandOpen && selectedIndex >= 0 && selectedIndex < allItems.length) {
			const item = allItems[selectedIndex]
			if (typeof item.onClick === "function") {
				item.onClick()
			} else if (item.onClick) {
				navigateToView(item.onClick, item.name)
			}
		}
	})

	return (
		<>
			<div className="p-2 pb-2">
				<div className="relative flex items-center">
					<Search
						className="absolute left-3 top-1/2 w-4 h-4 text-[#888]"
						style={{ transform: "translateY(-50%) translateZ(0)" }}
					/>
					<input
						ref={inputRef}
						type="text"
						onBlur={() => {
							// Use requestAnimationFrame to prevent layout shift during animation
							requestAnimationFrame(() => {
								window.dispatchEvent(
									new CustomEvent("command-menu-focus", {
										detail: { isFocused: false, hostElement: null },
									}),
								)
							})
						}}
						onFocus={(e) => {
							// Use requestAnimationFrame to prevent layout shift during animation
							requestAnimationFrame(() => {
								window.dispatchEvent(
									new CustomEvent("command-menu-focus", {
										detail: { isFocused: true, hostElement: e.currentTarget },
									}),
								)
							})
						}}
						placeholder="Search for apps and commands..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="w-full bg-white/60 rounded-md pl-10 pr-12 py-3 placeholder-[#888] text-sm text-black focus:outline-none"
						style={{
							transform: "translateZ(0)",
							willChange: "auto",
						}}
					/>
					<kbd
						onClick={() => setCommandOpen(false)}
						className="absolute right-3 top-1/2 px-1 py-0.5 rounded text-[10px] kbd !bg-neutral-50"
						style={{ transform: "translateY(-50%) translateZ(0)" }}
					>
						ESC
					</kbd>
				</div>
			</div>

			<div
				ref={scrollContainerRef}
				className={cn("flex-1 overflow-y-auto px-2", "scroll_area")}
			>
				{filteredSuggestions.length > 0 && (
					<div className="mb-4">
						<h3 className="text-[#666] text-xs font-medium mb-2 px-2 uppercase tracking-wider">
							Suggestions
						</h3>
						<div className="space-y-0">
							{filteredSuggestions.map((item, index) => (
								<CommandItemComponent
									key={`suggestion-${index}`}
									{...item}
									selected={selectedIndex === index}
									ref={(el) => {
										selectedItemRefs.current[index] = el
									}}
									onClick={() => {
										if (typeof item.onClick === "function") {
											item.onClick()
										} else if (item.onClick) {
											navigateToView(item.onClick, item.name)
										}
									}}
								/>
							))}
						</div>
					</div>
				)}

				{filteredCommands.length > 0 && (
					<div className="mb-4">
						<h3 className="text-[#666] text-xs font-medium mb-2 px-2 uppercase tracking-wider">
							{navigation.breadcrumb.length === 0
								? "More"
								: navigation.breadcrumb[navigation.breadcrumb.length - 1]}
						</h3>
						<div className="space-y-0">
							{filteredCommands.map((item, index) => {
								const actualIndex = filteredSuggestions.length + index
								return (
									<CommandItemComponent
										key={`command-${index}`}
										selected={selectedIndex === actualIndex}
										{...item}
										ref={(el) => {
											selectedItemRefs.current[actualIndex] = el
										}}
										onClick={() => {
											if (typeof item.onClick === "function") {
												item.onClick()
											} else if (item.onClick) {
												navigateToView(item.onClick, item.name)
											}
										}}
									/>
								)
							})}
						</div>
					</div>
				)}

				{filteredSuggestions.length === 0 &&
					filteredCommands.length === 0 &&
					searchQuery && (
						<div className="text-center py-8">
							<p className="text-[#999] text-sm">No results found</p>
						</div>
					)}
			</div>
		</>
	)
}
